import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'

/** The watch screen's phone layout, below 768px (the plan's Task 26). The sheets switch to drawers here too. */
export const PHONE_QUERY = '(max-width: 767px)'

/**
 * A phone on its side (the plan's Task 26): the video fills the screen. Wins over both other layouts. Short and at least
 * 3:2 wide (844×390 is 2.16, 667×375 1.78), so an upright phone whose viewport the keyboard shrinks (360×300 in an
 * Android in-app browser or split screen) stays upright; and a touch screen, so a short desktop window keeps the desktop.
 */
export const LANDSCAPE_QUERY = '(orientation: landscape) and (max-height: 500px) and (min-aspect-ratio: 3/2) and (pointer: coarse)'

/** A pointer that hovers (a mouse or trackpad), where a control can slide out on hover. A touch screen doesn't. */
export const HOVER_QUERY = '(hover: hover) and (pointer: fine)'

/** How the watch screen lays out: two columns, the phone's tabs, or the phone on its side. */
export type WatchLayout = 'desktop' | 'phone' | 'landscape'

function mediaQuery(query: string) {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query) : null
}

/** Whether a CSS media query matches now, following changes. False where `matchMedia` is missing. */
export function useMediaQuery(query: string) {
  // One list per query: its `matches` is live, so the snapshot reads it rather than building a list each render.
  const list = useMemo(() => mediaQuery(query), [query])
  const subscribe = useCallback(
    (onChange: () => void) => {
      list?.addEventListener('change', onChange)
      return () => list?.removeEventListener('change', onChange)
    },
    [list],
  )
  return useSyncExternalStore(subscribe, () => Boolean(list?.matches), () => false)
}

/**
 * The watch screen's layout now, following rotation and resizing. watch.css keys every layout rule on the page's
 * `data-layout`, so the stylesheet and the page never disagree about which one is showing. A phone on its side is
 * wider than the phone breakpoint, so landscape is checked first.
 */
export function useWatchLayout(): WatchLayout {
  const phone = useMediaQuery(PHONE_QUERY)
  const landscape = useMediaQuery(LANDSCAPE_QUERY)
  return landscape ? 'landscape' : phone ? 'phone' : 'desktop'
}

/** Input types that take typing, so the on-screen keyboard opens for them (not buttons, checkboxes and the like). */
const TYPED_INPUTS = new Set(['text', 'search', 'email', 'url', 'tel', 'password', 'number'])

/** Whether `target` is a field the viewer types in on the watch screen (inside `.watch-screen`). */
function typingOnScreen(target: EventTarget | null) {
  if (!(target instanceof HTMLElement) || !target.closest('.watch-screen')) return false
  if (target instanceof HTMLInputElement) return TYPED_INPUTS.has(target.type)
  return target instanceof HTMLTextAreaElement || target.isContentEditable
}

/**
 * Whether the viewer is typing in a field on the watch screen (`typingOnScreen`), following focus. While true the page
 * holds what would move or hide that field (`useHeld`): its layout, as `WatchSheet` holds while open, and the phone's
 * tab. The keyboard can squeeze the viewport into another layout's shape, and something can go live mid-message; a
 * switch then would move or hide the field, and the keyboard would close with it.
 */
export function useTypingOnScreen(): boolean {
  const [typing, setTyping] = useState(() => typeof document !== 'undefined' && typingOnScreen(document.activeElement))
  useEffect(() => {
    const onFocusIn = (event: FocusEvent) => setTyping(typingOnScreen(event.target))
    const onFocusOut = (event: FocusEvent) => setTyping(typingOnScreen(event.relatedTarget))
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
    }
  }, [])
  // A field removed while focused sends no focusout; it holds nothing once it has gone.
  return typing && typingOnScreen(document.activeElement)
}

/** `value`, kept at what it was while `hold` is true; it follows `value` again once released. */
export function useHeld<T>(value: T, hold: boolean): T {
  const [held, setHeld] = useState(value)
  if (!hold && held !== value) setHeld(value)
  return hold ? held : value
}
